import { useParams, useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  faCircleDollar,
  faMoneyBills,
  faLandmark,
  faReceipt,
} from '@fortawesome/pro-light-svg-icons'
import {
  Accordion,
  Card,
  Icon,
  Text,
  List,
  Link,
  CardColors,
  Breadcrumb,
} from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'

import { DATE_FORMATS } from '@/utils/dateHelpers'
import { centsToDollars } from '@/utils/currencyHelpers'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { useReselector } from '@/utils/sharedHooks'
import { selectTaxCalculationByTaxQuarter } from '@/selectors/userTaxCalculation.selectors'
import { selectStateEstimateByTaxQuarterAndState } from '@/features/taxes/quarterly/userTaxEstimates.selector'
import { useBreakdownTransparency } from './useBreakdownTransparency'
import { UserTaxCalculation } from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import {
  FILING_STATUSES,
  TAX_ENTITY_TYPES,
} from '@/features/taxes/taxConstants'

const taxCalculationQtrDisplayDate = (calc: UserTaxCalculation) => {
  const quarter = calc.calendarQuarter
  const year = calc.calendarYear
  switch (quarter) {
    case '1':
      return `January - February ${year}`
    case '2':
      return `January - April ${year}`
    case '3':
      return `January - July ${year}`
    case '4':
      return `January - November ${year}`
    default:
      return ''
  }
}

const UpdateProfileMessage = () => (
  <Text
    variant={TypographyStyles.BODY_SM}
    color={TextColors.MUTED}
    className="mt-2"
  >
    If these numbers don&apos;t look accurate, please reach out via{' '}
    <Link trackMessage="clicked qte reachout" href="/conversations">
      Messages
    </Link>
    .
  </Text>
)

const NetProfitCard = ({
  taxCalc,
}: {
  taxCalc?: UserTaxCalculation | null
}) => {
  const netIncomeForPeriod =
    (taxCalc?.periodIncomeInCents || 0) - (taxCalc?.periodExpensesInCents || 0)
  const netProfit = centsToDollars(netIncomeForPeriod)
  const netProfitText = taxCalc ? (
    <>
      {' '}
      of <CurrencyFormatLabel value={netProfit} /> from{' '}
      {taxCalculationQtrDisplayDate(taxCalc)}{' '}
    </>
  ) : null
  return (
    <Card color={CardColors.LEVEL_1} className="p-6 shadow sm:rounded-lg">
      <div className="flex flex-col sm:flex-row sm:items-start sm:gap-6">
        {/* Icon Column */}
        <div className="flex-none">
          <Icon icon={faCircleDollar} color="green" className="h-16 w-16" />
        </div>

        {/* Content Column */}
        <div className="flex-1 space-y-4">
          <Text variant={TypographyStyles.H2}>Net Profit</Text>

          <Text variant={TypographyStyles.BODY_SM}>
            Your <Link href="/reports/yearly">net profit</Link> {netProfitText}{' '}
            provides the <b>base number</b> for your estimate.
          </Text>

          <Text variant={TypographyStyles.BODY_SM}>
            We predict your annual net profit from this number in order to
            calculate your estimate.
          </Text>

          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            Uncategorized <Link href="/transactions/review">transactions</Link>{' '}
            and missing{' '}
            <Link href="/practice/documents">monthly bank statements</Link> can
            affect the accuracy of your net profit.
          </Text>
        </div>
      </div>
    </Card>
  )
}

const AdditionalTaxableIncomeCard = ({
  taxCalc,
}: {
  taxCalc?: UserTaxCalculation | null
}) => {
  const isScorp = taxCalc?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s

  return (
    <Card color={CardColors.LEVEL_1} className="p-6 shadow sm:rounded-lg">
      <div className="flex flex-col sm:flex-row sm:gap-6">
        {/* Icon */}
        <div className="flex-none">
          <Icon icon={faMoneyBills} color="green" className="h-16 w-16" />
        </div>

        {/* Content */}
        <div className="flex-1 space-y-4">
          <Text variant={TypographyStyles.H2}>Personal taxable income</Text>

          <Text variant={TypographyStyles.BODY_SM}>
            Your estimate is <b>increased</b> by any taxable income
            {isScorp ? ', both from and outside ' : ' from outside '}
            your private practice.
          </Text>

          {taxCalc && (
            <List className="list-disc list-inside space-y-1">
              {isScorp && (
                <List.Item>
                  <b>
                    <CurrencyFormatLabel
                      value={centsToDollars(
                        taxCalc.scorpPracticeWIncomeInCents
                      )}
                    />
                  </b>{' '}
                  of private practice YTD W-2 income
                </List.Item>
              )}
              <List.Item>
                <b>
                  <CurrencyFormatLabel
                    value={centsToDollars(taxCalc.wIncomeInCents)}
                  />
                </b>{' '}
                of {isScorp && 'other '}YTD W-2 income
              </List.Item>
              <List.Item>
                <b>
                  <CurrencyFormatLabel
                    value={centsToDollars(taxCalc.otherIndividualIncomeInCents)}
                  />
                </b>{' '}
                of YTD 1099 income
              </List.Item>
              {taxCalc.filingStatus ===
                FILING_STATUSES.married_filing_jointly && (
                <>
                  <List.Item>
                    <b>
                      <CurrencyFormatLabel
                        value={centsToDollars(taxCalc.spouseWIncomeInCents)}
                      />
                    </b>{' '}
                    of your spouse’s YTD W-2 income
                  </List.Item>
                  <List.Item>
                    <b>
                      <CurrencyFormatLabel
                        value={centsToDollars(
                          taxCalc.spouseIndividualIncomeInCents
                        )}
                      />
                    </b>{' '}
                    of your spouse’s YTD 1099 income
                  </List.Item>
                </>
              )}
            </List>
          )}

          <Text variant={TypographyStyles.BODY_SM}>
            We predict your annual W-2 income from the number(s) above in order
            to calculate your estimate.
          </Text>

          <Accordion
            variant="text"
            type="single"
            collapsible
            className="w-full"
          >
            <Accordion.Item value="rental-income">
              <Accordion.Trigger className="w-full text-left">
                What if I have income from rental property or investments?
              </Accordion.Trigger>
              <Accordion.Content>
                <Text variant={TypographyStyles.BODY_SM}>
                  The Heard Tax Team doesn’t include either in your quarterly
                  tax calculation, but we suggest putting money aside for annual
                  taxes. The same goes for capital gains, pension distributions
                  and other income that isn’t W-2 or 1099.
                </Text>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>

          <UpdateProfileMessage />
        </div>
      </div>
    </Card>
  )
}

const WithholdingsCard = ({
  taxCalc,
}: {
  taxCalc?: UserTaxCalculation | null
}) => (
  <Card color={CardColors.LEVEL_1} className="p-6 shadow sm:rounded-lg">
    <div className="flex flex-col sm:flex-row sm:gap-6">
      <div className="flex-none">
        <Icon icon={faLandmark} color="green" className="h-16 w-16" />
      </div>

      <div className="flex-1 space-y-4">
        <Text variant={TypographyStyles.H2}>Withholdings</Text>

        <Text variant={TypographyStyles.BODY_SM}>
          Your estimate is <b>reduced</b> by any withholdings. If you’re an S
          corporation owner, this includes W-2 withholdings from being an
          employee of your private practice.
        </Text>

        {taxCalc && (
          <List className="list-disc list-inside space-y-1">
            <List.Item>
              <b>
                {/* <CurrencyFormatLabel
                    value={centsToDollars(taxCalc.federalWithholdingInCents)}
                  /> */}
              </b>{' '}
              of your projected federal withholdings
            </List.Item>
            <List.Item>
              <b>
                {/* <CurrencyFormatLabel
                    value={centsToDollars(taxCalc.stateWithholdingInCents)}
                  /> */}
              </b>{' '}
              of your projected state withholdings
            </List.Item>
            {taxCalc.filingStatus ===
              FILING_STATUSES.married_filing_jointly && (
              <>
                <List.Item>
                  <b>
                    <CurrencyFormatLabel
                      value={centsToDollars(
                        taxCalc.spouseFederalWithholdingInCents
                      )}
                    />
                  </b>{' '}
                  of your spouse’s projected federal withholdings
                </List.Item>
                <List.Item>
                  <b>
                    <CurrencyFormatLabel
                      value={centsToDollars(
                        taxCalc.spouseStateWithholdingInCents
                      )}
                    />
                  </b>{' '}
                  of your spouse’s projected state withholdings
                </List.Item>
              </>
            )}
          </List>
        )}
        <UpdateProfileMessage />
      </div>
    </div>
  </Card>
)

const PreviousQuarterlyPaymentsCard = () => (
  <Card color={CardColors.LEVEL_1} className="p-6 shadow sm:rounded-lg">
    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
      {/* Icon Column */}
      <div className="flex-none">
        <Icon icon={faReceipt} color="green" className="h-16 w-16" />
      </div>

      {/* Text content */}
      <div className="flex-1 space-y-2">
        <Text variant={TypographyStyles.H2}>Previous Quarterly Payments</Text>

        <Text variant={TypographyStyles.BODY_SM}>
          Tax payments made in Q1-Q3 are used to calculate an accurate Q4
          estimate.
        </Text>

        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          Make sure all tax payments throughout the year are recorded in your{' '}
          <Link href="/taxes/quarterly" variant="secondary">
            Payment History
          </Link>
          .
        </Text>
      </div>
    </div>
  </Card>
)

export const DefaultBreakdown = ({ filingState }: { filingState?: string }) => {
  const { taxQuarter } = useParams()
  const taxCalc = useReselector(selectTaxCalculationByTaxQuarter, taxQuarter)
  const navigate = useNavigate()
  const transparencyEnabled = useBreakdownTransparency()

  const stateEstimate = useReselector(
    selectStateEstimateByTaxQuarterAndState,
    taxQuarter,
    filingState
  )
  const shownState = transparencyEnabled ? filingState : undefined

  return (
    <div className="space-y-8 px-4 md:px-8 lg:px-16">
      {/* Breadcrumb */}
      <Breadcrumb className="mb-6">
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link onClick={() => navigate('/taxes/quarterly')}>
              Quarterly Taxes
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Page>
              How are your quarterly tax estimates calculated?
            </Breadcrumb.Page>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb>

      {/* Title */}
      <Text variant={TypographyStyles.H1}>
        How are your quarterly tax estimates calculated?
      </Text>

      {shownState && taxCalc?.calendarQuarter && (
        <div className="space-y-1">
          <Text variant={TypographyStyles.H2}>
            Q{taxCalc.calendarQuarter} {shownState} state tax estimate
          </Text>
          {stateEstimate?.estimateInCents !== undefined &&
            stateEstimate?.estimateInCents !== null && (
              <Text variant={TypographyStyles.H3}>
                <CurrencyFormatLabel
                  value={centsToDollars(stateEstimate.estimateInCents)}
                />
              </Text>
            )}
        </div>
      )}

      {/* Description */}
      <div className="space-y-2 text-gray-700">
        <Text variant={TypographyStyles.BODY_SM}>
          We predict your total {taxCalc?.calendarYear} tax liability based upon
          your financial results so far, and then adjust for this quarter’s
          payment.
        </Text>
        {taxCalc?.calculatedAt && (
          <Text variant={TypographyStyles.BODY_SM}>
            The dollar values you see below reflect the values we used to
            calculate this estimate on{' '}
            {moment(taxCalc.calculatedAt).format(DATE_FORMATS.DISPLAY_SHORT)}.
          </Text>
        )}
      </div>

      {/* Cards laid out responsively */}
      <div className="flex flex-col">
        <NetProfitCard taxCalc={taxCalc} />
        <AdditionalTaxableIncomeCard taxCalc={taxCalc} />
        <WithholdingsCard taxCalc={taxCalc} />
        <PreviousQuarterlyPaymentsCard />
      </div>
    </div>
  )
}
